import { useEffect, useState } from "react";
import axios from "../api/axios";

const editableFields = [
  ["name", "Full name"],
  ["mobile", "Mobile number"],
  ["email", "Email address"],
  ["dateOfBirth", "Date of birth"],
  ["address", "Address"],
  ["state", "State"],
  ["district", "District"],
  ["tahsil", "Tahsil"],
  ["postOffice", "Post office"],
  ["pinCode", "PIN code"],
  ["nomineeName", "Nominee name"],
  ["nomineeRelation", "Nominee relation"],
  ["nomineePhone", "Nominee phone"],
];

const accountFields = [
  ["uniqueId", "User ID"],
  ["lbbpId", "LBBP ID"],
  ["puapId", "PU/AP ID"],
  ["role", "Role"],
  ["userType", "User type"],
  ["status", "Status"],
  ["kycStatus", "KYC status"],
  ["sponsorId", "Sponsor"],
  ["createdAt", "Registered on"],
];

const show = (name, value) => {
  if (value === undefined || value === null || value === "") return "—";
  if (name === "dateOfBirth" || name === "createdAt") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
  }
  return typeof value === "object" ? String(value._id || value.uniqueId || "—") : String(value);
};

const ProfileFieldPermissionModal = ({ user, onClose }) => {
  const [details, setDetails] = useState(null);
  const [enabled, setEnabled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    axios.get(`/profile-updates/admin/permissions/${user._id}`)
      .then(({ data }) => {
        if (!active) return;
        setDetails(data);
        setEnabled(data.allowedFields || []);
      })
      .catch((e) => active && setError(e.response?.data?.message || "Unable to load user details."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [user._id]);

  const toggle = (name) => setEnabled((current) =>
    current.includes(name) ? current.filter((f) => f !== name) : [...current, name]);

  const submit = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const { data } = await axios.put(`/profile-updates/admin/permissions/${user._id}`, { fields: enabled });
      setNotice(data.message);
    } catch (e) {
      setError(e.response?.data?.message || "Unable to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  const u = details?.user;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5 text-left">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-slate-800">Profile update access — {user.name}</h3>
          <button onClick={onClose} aria-label="Close" className="text-2xl leading-none text-slate-500 hover:text-slate-800">×</button>
        </div>

        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {error && <div role="alert" className="mb-3 rounded bg-red-50 p-2 text-sm text-red-700">{error}</div>}
        {notice && <div role="status" className="mb-3 rounded bg-green-50 p-2 text-sm text-green-700">{notice}</div>}

        {u && (
          <>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Account details</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              {accountFields.map(([name, label]) => (
                <div key={name} className="rounded bg-slate-50 p-2">
                  <div className="text-xs text-slate-500">{label}</div>
                  <div className="text-sm text-slate-800 break-words">{show(name, u[name])}</div>
                </div>
              ))}
            </div>

            <h4 className="text-sm font-semibold text-slate-700 mb-1">Profile fields</h4>
            <p className="text-xs text-slate-500 mb-2">
              Turn on the fields this user is allowed to update. Changes still need your approval.
            </p>
            {details.hasPendingRequest && (
              <p className="text-xs text-amber-700 mb-2">This user has a pending request; changes here apply to their next request.</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {editableFields.map(([name, label]) => (
                <label key={name} className="flex items-center justify-between gap-3 rounded border border-slate-200 p-2 cursor-pointer">
                  <span>
                    <span className="block text-xs text-slate-500">{label}</span>
                    <span className="block text-sm text-slate-800 break-words">{show(name, u[name])}</span>
                  </span>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={enabled.includes(name)}
                    onChange={() => toggle(name)}
                    className="h-5 w-9 cursor-pointer accent-emerald-600"
                    aria-label={`Allow update of ${label}`}
                  />
                </label>
              ))}
            </div>

            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={onClose} className="rounded bg-slate-200 px-4 py-1.5 text-sm hover:bg-slate-300">Close</button>
              <button onClick={submit} disabled={saving} className="rounded bg-emerald-600 px-4 py-1.5 text-sm text-white hover:bg-emerald-700 disabled:opacity-60">
                {saving ? "Saving…" : "Submit"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileFieldPermissionModal;
